import { Result } from "@swan-io/boxed";
import { ResultOf } from "gql.tada";
import { useEffect, useState } from "react";
import { StyleSheet, View } from "react-native";
import { hasDefinedKeys, useForm } from "react-ux-form";
import { P, match } from "ts-pattern";
import { AppBackLink, DangerZoneTitle } from "../components/AppLayout";
import { EditAvatarButton, UserAvatar } from "../components/Avatar";
import { Box } from "../components/Box";
import { Button } from "../components/Button";
import { FullScreenScrollView } from "../components/FullScreenScrollView";
import { InputFile } from "../components/InputFile";
import { InputText } from "../components/InputText";
import { ConfirmContent, Modal } from "../components/Modal";
import { Redirect } from "../components/Redirect";
import { Space } from "../components/Space";
import { Label, Text } from "../components/Text";
import { toast } from "../components/Toast";
import { useResponsive } from "../hooks/useResponsive";
import { useTitle } from "../hooks/useTitle";
import { ApplicationKey, applications } from "../utils/applications";
import { colors, invariantColor, radii } from "../utils/design";
import { readAsBase64 } from "../utils/file";
import {
  fetch,
  graphql,
  mutate,
  setQueryData,
  usePromise,
  usePromiseMutation,
  useQuery,
} from "../utils/graphql";
import { t, tNested } from "../utils/i18n";
import { MeFragment, MeQuery, UsernameExistsQuery } from "../utils/queries";
import { router } from "../utils/routes";
import { uploadUserAvatar } from "../utils/upload";
import { NAME_MAX_LENGTH, validateName, validateSlug } from "../utils/validation";

const styles = StyleSheet.create({
  previewContainer: {
    paddingHorizontal: 24,
    paddingVertical: 24,
    flexDirection: "row",
    alignItems: "center",
    borderRadius: radii[4],
    backgroundColor: colors.neutral[800],
  },
  modalPreview: {
    backgroundColor: colors.neutral[900],
  },

  fill: {
    flex: 1,
  },

  apiKeyButton: {
    top: 27, // to align with the input text
    height: 46, // to fit with input text height
    width: 46, // to make it a square
  },
});

const UpdateMeMutation = graphql(
  `
    mutation UpdateMeMutation($input: UpdateUserInput!) {
      updateMe(input: $input) {
        ...MeFragment
      }
    }
  `,
  [MeFragment],
);

const DeleteMeMutation = graphql(`
  mutation DeleteMeMutation {
    deleteMe {
      id
    }
  }
`);

const RefreshTokenMutation = graphql(
  `
    mutation RefreshTokenMutation {
      refreshPrivateApiKey {
        privateApiKey
      }
    }
  `,
  [],
);

type Props = {
  fromApp: ApplicationKey | undefined;
};

export const MyProfilePage: React.FC<Props> = ({ fromApp }) => {
  useTitle(t("page_title.my_profile"), fromApp);

  const {
    data: { me, apiKeys },
    refetch: refetchMe,
  } = useQuery(MeQuery);
  const { data: updateResult, execute, reset: resetMutationState } = usePromise();

  const [uploadAvatarOpened, setUploadAvatarOpened] = useState(false);
  const [deleteAccountOpened, setDeleteAccountOpened] = useState(false);
  const [refreshTokenOpened, setRefreshTokenOpened] = useState(false);

  const { Field, FieldsListener, submitForm, formStatus, resetForm } = useForm({
    fullName: {
      initialValue: me.fullName,
      sanitize: (value) => value.trim(),
      validate: (value) => {
        return match(validateName(value))
          .with({ type: "TOO_SHORT" }, ({ minLength }) =>
            t("my_profile.general.full_name.too_short", { count: minLength }),
          )
          .with({ type: "TOO_LONG" }, ({ maxLength }) =>
            t("my_profile.general.full_name.too_long", { count: maxLength }),
          )
          .with({ type: "INVALID" }, () => t("my_profile.general.full_name.description"))
          .with(P.nullish, () => undefined)
          .exhaustive();
      },
    },
    username: {
      initialValue: me.username,
      sanitize: (value) => value.trim(),
      validate: async (value) => {
        const invalidMessage = match(validateSlug(value))
          .with({ type: "TOO_SHORT" }, ({ minLength }) =>
            t("my_profile.general.username.too_short", { count: minLength }),
          )
          .with({ type: "TOO_LONG" }, ({ maxLength }) =>
            t("my_profile.general.username.too_long", { count: maxLength }),
          )
          .with({ type: "INVALID" }, () => t("my_profile.general.username.invalid"))
          .with(P.nullish, () => undefined)
          .exhaustive();

        if (invalidMessage != null) {
          return invalidMessage;
        }

        // check if username is already taken
        const result = await Result.fromPromise(
          fetch({ document: UsernameExistsQuery, variables: { username: value } }),
        );

        return match(result)
          .with(Result.P.Ok({ usernameExists: true }), () =>
            t("my_profile.general.username.already_taken"),
          )
          .with(Result.P.Error(P.any), () => t("my_profile.general.username.error"))
          .otherwise(() => undefined);
      },
    },
  });

  const handleSubmit = () =>
    submitForm((values) => {
      if (!hasDefinedKeys(values, ["fullName", "username"])) {
        return;
      }

      const { fullName, username } = values;

      return execute(async () => {
        const result = await mutate(UpdateMeMutation, {
          input: {
            fullName,
            username,
          },
        });

        result.match({
          Ok: ({ updateMe: me }) => {
            setQueryData({ me, apiKeys }, MeQuery);
            setTimeout(() => {
              resetForm();
              resetMutationState();
            }, 1000);
          },
          Error: () => {
            toast({
              variant: "error",
              title: t("my_profile.general.error.update_user.title"),
              message: t("my_profile.general.error.update_user.message"),
            });
          },
        });

        return result;
      });
    });

  if (fromApp != null && updateResult.isDone() && updateResult.value.isOk()) {
    return <Redirect to={`${applications[fromApp].url}?from=profile_updated`} />;
  }

  return (
    <FullScreenScrollView maxWidth={535}>
      {fromApp != null && (
        <>
          <AppBackLink
            to={applications[fromApp].url}
            label={t("my_profile.back_to_app", { name: applications[fromApp].name })}
          />

          <Space height={12} />
        </>
      )}

      <Text variant="title2" color={invariantColor.white}>
        {t("my_profile.title")}
      </Text>
      <Space height={8} />
      <Text variant="body">{t("my_profile.description")}</Text>

      <Space height={24} />

      <View style={styles.previewContainer}>
        <EditAvatarButton type="user" onPress={() => setUploadAvatarOpened(true)}>
          <UserAvatar
            size="extra-large"
            name={me.fullName}
            url={me.avatarUrl}
            updatedAt={me.updatedAt}
          />
        </EditAvatarButton>

        <Space width={16} />

        <View>
          <FieldsListener names={["fullName", "username"]}>
            {({ fullName }) => (
              <Text variant="title3" color={invariantColor.white}>
                {fullName.value}
              </Text>
            )}
          </FieldsListener>
          <Text variant="body">{me.email}</Text>
        </View>
      </View>

      <Space height={24} />

      <Field name="fullName">
        {({ value, error, onChange, onBlur }) => (
          <InputText
            label={t("my_profile.general.full_name.label")}
            description={t("my_profile.general.full_name.description")}
            placeholder={t("my_profile.general.full_name.placeholder")}
            aria-required={true}
            maxLength={NAME_MAX_LENGTH}
            value={value}
            error={error}
            onChangeText={onChange}
            onBlur={onBlur}
          />
        )}
      </Field>

      <Space height={24} />

      <Field name="username">
        {({ value, error, validating, onChange, onBlur }) => (
          <InputText
            label={t("my_profile.general.username.label")}
            description={t("my_profile.general.username.description")}
            placeholder={t("my_profile.general.username.placeholder")}
            aria-required={true}
            maxLength={NAME_MAX_LENGTH}
            value={value}
            error={error}
            validating={validating}
            onChangeText={onChange}
            onBlur={onBlur}
          />
        )}
      </Field>

      <Space height={24} />

      <Button
        disabled={formStatus === "untouched"}
        status={updateResult}
        selfAlign="start"
        onPress={handleSubmit}
      >
        {t("my_profile.general.submit")}
      </Button>

      <Space height={24} />

      <Box direction="row" alignItems="flex-start">
        <InputText
          style={styles.fill}
          label={t("my_profile.general.private_api_key.label")}
          value={apiKeys.privateApiKey}
          hideError={true}
        />

        <Space width={8} />

        <Button
          style={styles.apiKeyButton}
          size="small"
          variant="transparent"
          onPress={() => {
            navigator.clipboard.writeText(apiKeys.privateApiKey);
            toast({
              variant: "success",
              title: t("my_profile.general.private_api_key.copied"),
              message: t("my_profile.general.private_api_key.copied_message"),
            });
          }}
          icon="copy"
        />

        <Space width={8} />

        <Button
          style={styles.apiKeyButton}
          size="small"
          variant="transparent"
          onPress={() => setRefreshTokenOpened(true)}
          icon="reload"
        />
      </Box>
      <Space height={4} />
      <Text variant="small" color={colors.neutral[200]}>
        {t("my_profile.general.private_api_key.description")}
      </Text>

      <Space height={36} />

      <DangerZoneTitle
        title={t("my_profile.delete_account.title")}
        description={t("my_profile.delete_account.description")}
      />

      <Space height={24} />

      <Button variant="negative" selfAlign="start" onPress={() => setDeleteAccountOpened(true)}>
        {t("my_profile.delete_account.button")}
      </Button>

      <Modal>
        {uploadAvatarOpened ? (
          <UploadAvatarForm
            me={me}
            onClosed={() => setUploadAvatarOpened(false)}
            onAvatarUploaded={refetchMe}
          />
        ) : null}
      </Modal>

      <Modal>
        {deleteAccountOpened ? (
          <DeleteAccountForm username={me.username} onClose={() => setDeleteAccountOpened(false)} />
        ) : null}
      </Modal>

      <Modal>
        {refreshTokenOpened ? (
          <RefreshApiTokenModale me={me} onClose={() => setRefreshTokenOpened(false)} />
        ) : null}
      </Modal>
    </FullScreenScrollView>
  );
};

type UploadAvatarFormProps = {
  me: ResultOf<typeof MeQuery>["me"];
  onClosed: () => void;
  onAvatarUploaded: () => void;
};

const UploadAvatarForm: React.FC<UploadAvatarFormProps> = ({ me, onClosed, onAvatarUploaded }) => {
  const deviceSize = useResponsive();
  const isMobile = deviceSize === "mobile";

  const { data: uploadResult, mutate: upload } = usePromiseMutation(uploadUserAvatar);

  const [file, setFile] = useState<File | null>(null);
  const [fileBase64, setFileBase64] = useState<string | null>(null);

  useEffect(() => {
    if (file != null) {
      readAsBase64(file).then(setFileBase64);
    } else {
      setFileBase64(null);
    }
  }, [file]);

  const handleSubmit = async () => {
    if (file == null) {
      return;
    }

    const result = await upload(file);

    result.match({
      Ok: () => {
        onAvatarUploaded();
        onClosed();
      },
      Error: () => {
        toast({
          variant: "error",
          title: t("my_profile.avatar.error.upload_avatar.title"),
          message: t("my_profile.avatar.error.upload_avatar.message"),
        });
      },
    });
  };

  return (
    <>
      <Text variant="title3" color={invariantColor.white}>
        {t("my_profile.avatar.title")}
      </Text>

      <Space height={24} />

      <InputFile smallUI={isMobile} fileType="image" value={file} onChange={setFile} />

      <Space height={24} />

      <Label>{t("common.preview")}</Label>
      <Space height={8} />

      <View style={[styles.previewContainer, styles.modalPreview]}>
        <UserAvatar
          size="extra-large"
          name={me.fullName}
          url={fileBase64 ?? me.avatarUrl}
          updatedAt={fileBase64 != null ? undefined : me.updatedAt}
        />

        <Space width={16} />

        <View>
          <Text variant="title3" color={invariantColor.white}>
            {me.fullName}
          </Text>
          <Text variant="body" color={colors.primary[500]}>
            @{me.username}
          </Text>
        </View>
      </View>

      <Space height={36} />

      <Box direction="row" alignItems="center">
        <Button
          disabled={uploadResult.isLoading()}
          variant="outline"
          style={styles.fill}
          onPress={onClosed}
        >
          {t("common.cancel")}
        </Button>

        <Space width={12} />

        <Button
          status={uploadResult}
          disabled={file == null}
          style={styles.fill}
          onPress={handleSubmit}
        >
          {t("my_profile.avatar.submit")}
        </Button>
      </Box>
    </>
  );
};

type DeleteAccountFormProps = {
  username: string;
  onClose: () => void;
};

const DeleteAccountForm: React.FC<DeleteAccountFormProps> = ({ username, onClose }) => {
  const { data: deleteResult, execute } = usePromise();

  const { Field, FieldsListener, submitForm } = useForm({
    username: {
      initialValue: "",
      validate: (value) => {
        if (value !== username) {
          // we don't display any error message
          return "";
        }
      },
    },
  });

  const deleteAccount = () =>
    submitForm((values) => {
      if (!hasDefinedKeys(values, ["username"])) {
        return;
      }

      return execute(async () => {
        const result = await mutate(DeleteMeMutation);

        result.match({
          Ok: () => {
            router.push("Signout");
          },
          Error: () => {
            toast({
              variant: "error",
              title: t("my_profile.delete_account.error.title"),
              message: t("my_profile.delete_account.error.message"),
            });
          },
        });

        return result;
      });
    });

  return (
    <>
      <Text variant="title3" color={invariantColor.white}>
        {t("my_profile.delete_account.modal.title")}
      </Text>

      <Space height={8} />

      <Text variant="small">
        {tNested("my_profile.delete_account.modal.description", {
          link: (value) => <Text color={colors.primary[500]}>{value}</Text>,
        })}
      </Text>

      <Space height={24} />

      <Field name="username">
        {({ value, onChange, onBlur }) => (
          <InputText
            label={t("my_profile.delete_account.modal.username.label")}
            placeholder={username}
            value={value}
            hideError={true}
            onChangeText={onChange}
            onBlur={onBlur}
          />
        )}
      </Field>

      <Space height={24} />

      <Box direction="row" alignItems="center">
        <Button
          disabled={deleteResult.isLoading()}
          variant="outline"
          style={styles.fill}
          onPress={onClose}
        >
          {t("common.cancel")}
        </Button>

        <Space width={12} />

        <FieldsListener names={["username"]}>
          {({ username }) => (
            <Button
              status={deleteResult}
              disabled={!username.valid}
              style={styles.fill}
              onPress={deleteAccount}
            >
              {t("my_profile.delete_account.modal.submit")}
            </Button>
          )}
        </FieldsListener>
      </Box>
    </>
  );
};

type RefreshApiTokenModaleProps = {
  me: ResultOf<typeof MeQuery>["me"];
  onClose: () => void;
};

const RefreshApiTokenModale: React.FC<RefreshApiTokenModaleProps> = ({ me, onClose }) => {
  const { data: result, execute } = usePromise();

  const refreshApiToken = () => {
    return execute(async () => {
      const result = await mutate(RefreshTokenMutation);

      result.match({
        Ok: ({ refreshPrivateApiKey: apiKeys }) => {
          setQueryData({ me, apiKeys }, MeQuery);
          onClose();
        },
        Error: () => {
          toast({
            variant: "error",
            title: t("my_profile.general.error.refresh_api_key.title"),
            message: t("my_profile.general.error.refresh_api_key.message"),
          });
        },
      });

      return result;
    });
  };

  return (
    <ConfirmContent
      title={t("my_profile.general.refresh_api_key.title")}
      message={t("my_profile.general.refresh_api_key.message")}
      confirmText={t("my_profile.general.refresh_api_key.confirm")}
      cancelText={t("common.cancel")}
      status={result}
      onConfirm={refreshApiToken}
      onCancel={onClose}
    />
  );
};
